/**
 * DealVehiclesTab - Aba "Veículos" do modal premium de negócio
 *
 * Exibe os veículos vinculados ao negócio (crm_deal_vehicles) e permite
 * vincular/desvincular veículos já cadastrados no cliente/contato.
 * Todo o acesso a dados é feito por RPCs SECURITY DEFINER
 * (list_customer_vehicles / link_deal_vehicle), nunca via SELECT direto.
 *
 * @version 1.2.0
 * RELEASE: 2026-08-09 v1.2.0 - Botão "Novo Veículo" encapsulado na permissão de criação
 *            do menu de veículos (canCreate('vehicles_list'), admin com bypass)
 * RELEASE: 2026-08-09 v1.1.0 - Clique na linha abre o modal 360º do veículo e coluna
 *            "Tipo" passa a usar VehicleTypeCell (ícone + palavra), igual à aba de tarefas
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial (Fase 6 do módulo de Veículos)
 */

import React, { useMemo, useState } from 'react';
import { Car, Link2, Loader2, Plus, Unlink } from 'lucide-react';
// v1.1.0: coluna "Tipo" padronizada (ícone + palavra) e modal 360º do veículo
import { VehicleTypeCell } from '@/components/vehicles/VehicleTypeCell';
import { VehicleDetailsModalPremium } from '@/components/vehicles/modal/VehicleDetailsModalPremium';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCustomerVehicles,
  useDealVehicleLink,
  VEHICLE_TYPE_LABELS,
  type CustomerVehicle,
} from '@/hooks/useCustomerVehicles';
import { VehicleFormDialog } from '@/components/vehicles/VehicleFormDialog';
// v1.2.0: controle de permissão por perfil (RPC central de permissões)
import { usePermissionsContext } from '@/contexts/PermissionsContext';

/** Props da aba */
interface DealVehiclesTabProps {
  /** ID do negócio atual */
  dealId: string;
  /** Cliente vinculado ao negócio (quando existir) */
  customerId?: string | null;
  /** Contato vinculado ao negócio (fallback quando não há cliente) */
  contactId?: string | null;
  /** Aba visível (habilita as queries — lazy loading) */
  isActive: boolean;
  /** Bloqueia ações de vínculo (ex.: negócio fechado/sem permissão) */
  readOnly?: boolean;
}

/** Formata valor FIPE em BRL */
function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Rótulo curto do veículo (placa · marca · modelo) */
function vehicleLabel(v: CustomerVehicle): string {
  return [v.plate, v.brand_name, v.model_name].filter(Boolean).join(' · ') || 'Veículo';
}

export function DealVehiclesTab({
  dealId,
  customerId,
  contactId,
  isActive,
  readOnly = false,
}: DealVehiclesTabProps) {
  // v1.2.0: permissão de criação de veículo (admin tem bypass no contexto)
  const { canCreate, isAdmin } = usePermissionsContext();
  const canCreateVehicle = isAdmin || canCreate('vehicles_list');

  // Veículos já vinculados ao negócio
  const { vehicles: linked, isLoading } = useCustomerVehicles({ dealId }, isActive);

  // Veículos disponíveis no escopo do cliente/contato (para vincular)
  const { vehicles: available } = useCustomerVehicles(
    { customerId: customerId ?? null, contactId: contactId ?? null },
    isActive && !!(customerId || contactId),
  );

  const linkMutation = useDealVehicleLink();

  // Seleção do combo de vínculo
  const [selectedId, setSelectedId] = useState<string>('');
  // Modal de cadastro rápido de veículo
  const [formOpen, setFormOpen] = useState(false);
  // v1.1.0: veículo aberto no modal 360º (clique na linha)
  const [detailsVehicleId, setDetailsVehicleId] = useState<string | null>(null);

  /** Opções ainda não vinculadas ao negócio */
  const linkableOptions = useMemo(() => {
    const linkedIds = new Set(linked.map((v) => v.id));
    return available.filter((v) => !linkedIds.has(v.id));
  }, [available, linked]);

  /** Vincula o veículo selecionado ao negócio */
  const handleLink = async () => {
    if (!selectedId) return;
    await linkMutation.mutateAsync({ dealId, vehicleId: selectedId });
    setSelectedId('');
  };

  /** Remove o vínculo do veículo com o negócio */
  const handleUnlink = async (vehicleId: string) => {
    await linkMutation.mutateAsync({ dealId, vehicleId, unlink: true });
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Barra de ações: selecionar veículo existente + cadastrar novo */}
      {!readOnly && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="h-9 sm:max-w-sm">
              <SelectValue
                placeholder={
                  linkableOptions.length > 0
                    ? 'Selecione um veículo do cliente'
                    : 'Nenhum veículo disponível'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {linkableOptions.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {vehicleLabel(v)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            size="sm"
            onClick={handleLink}
            disabled={!selectedId || linkMutation.isPending}
            className="gap-1.5"
          >
            {linkMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4" />
            )}
            Vincular
          </Button>

          {/* v1.2.0: só exibe cadastro rápido com permissão de criação */}
          {canCreateVehicle && (customerId || contactId) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFormOpen(true)}
              className="gap-1.5 sm:ml-auto"
            >
              <Plus className="h-4 w-4" />
              Novo Veículo
            </Button>
          )}
        </div>
      )}

      {/* Listagem dos veículos vinculados */}
      {linked.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-10 text-center">
          <Car className="h-8 w-8 text-muted-foreground/60" />
          <div>
            <p className="text-sm font-medium">Nenhum veículo vinculado</p>
            <p className="text-xs text-muted-foreground">
              Vincule veículos para usá-los automaticamente na geração de contratos.
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[680px] text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Placa</th>
                <th className="px-3 py-2 text-left font-medium">Tipo</th>
                <th className="px-3 py-2 text-left font-medium">Marca / Modelo</th>
                <th className="px-3 py-2 text-left font-medium">Ano</th>
                <th className="px-3 py-2 text-right font-medium">Valor FIPE</th>
                {!readOnly && <th className="w-14 px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y">
              {linked.map((vehicle) => (
                <tr
                  key={vehicle.id}
                  className="cursor-pointer hover:bg-muted/30"
                  onClick={() => setDetailsVehicleId(vehicle.id)}
                >
                  <td className="px-3 py-2 font-mono font-medium">{vehicle.plate || '—'}</td>
                  <td className="px-3 py-2">
                    <VehicleTypeCell
                      type={vehicle.vehicle_type}
                      categoryName={vehicle.category_name}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {[vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-2">{vehicle.year ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(vehicle.fipe_value)}</td>
                  {!readOnly && (
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        title="Desvincular do negócio"
                        onClick={() => handleUnlink(vehicle.id)}
                        disabled={linkMutation.isPending}
                      >
                        <Unlink className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cadastro rápido: cria o veículo no escopo do cliente/contato do negócio */}
      <VehicleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        scope={{ customerId: customerId ?? null, contactId: contactId ?? null }}
        vehicle={null}
      />

      {/* v1.1.0: modal 360º do veículo (abre ao clicar na linha) */}
      {detailsVehicleId && (
        <VehicleDetailsModalPremium
          vehicleId={detailsVehicleId}
          open={!!detailsVehicleId}
          onOpenChange={(isOpen) => !isOpen && setDetailsVehicleId(null)}
        />
      )}
    </div>
  );
}
